import { highlightElStep, makeModalTour } from './tours.js'

const TOUR_SELECTOR = '[data-admin-campaign-builder-tour="true"]'

const TAB_STEPS = {
  new: [
    {
      title: "Create campaign",
      text: "We will build a community donation campaign. This is still a normal form template; choosing the type just builds the relevant starter questions for you.",
      selector: '[data-tour="campaign-settings-shell"]'
    },
    {
      title: "Title",
      text: "Enter a clear supporter-facing title. Example: Help fund emergency care for local animals.",
      selector: '[data-tour="campaign-settings-title"]',
      interactive: true,
      required: true
    },
    {
      title: "SEO description",
      text: "Add a search-friendly summary. Good example: Donate to help Sophie's Legacy fund urgent veterinary care, community outreach, and practical support for animals in need.",
      selector: '[data-tour="campaign-settings-seo"]',
      interactive: true,
      required: true
    },
    {
      title: "Owner name",
      text: "Enter the public organiser name shown on campaign cards. Use the person, team, or organisation supporters should recognise.",
      selector: '[data-tour="campaign-settings-owner"]',
      interactive: true
    },
    {
      title: "Public URL",
      text: "Enter a short, readable slug. Example: emergency-care-fund. This becomes the public /campaigns/ URL.",
      selector: '[data-tour="campaign-settings-slug"]',
      interactive: true,
      required: true,
      pattern: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
      invalidText: "Use lowercase letters, numbers, and single hyphens only."
    },
    {
      title: "Community donation",
      text: "Select Community Donation. It behaves like the same campaign form system, but creates the donation-focused starter questions and donation tracking setup.",
      selector: '[data-tour="campaign-settings-community-donation"]',
      interactive: true,
      validateSelector: '[data-tour="campaign-settings-community-donation-input"]',
      validate: el => el?.checked,
      invalidText: "Select Community Donation before continuing."
    },
    {
      title: "Create",
      text: "Create the campaign when the required fields are ready. The tutorial will continue in the editor after the template has created the starter questions.",
      selector: '[data-tour="campaign-settings-submit"]',
      interactive: true
    }
  ],
  editor: [
    ["Editor", "This is the campaign editor. It mirrors the public page so you can edit the campaign in context.", '[data-tour="campaign-editor-shell"]'],
    ["Story", "Use this area for the main campaign story supporters read before responding.", '[data-tour="campaign-editor-description"]'],
    ["Image", "Update the campaign image and positioning here. This affects the visual header on the public campaign.", '[data-tour="campaign-editor-image"]'],
    ["Goals", "Set the target and decide whether the progress bar is shown publicly.", '[data-tour="campaign-editor-goals"]'],
    ["Questions", "Questions live here on the page. The advanced Questions tab gives you more detailed controls.", '[data-tour="campaign-editor-questions-summary"]'],
    ["Completion", "Next we will set up what supporters see after submitting.", '[data-tour="campaign-builder-nav-completion"]']
  ],
  completion: [
    ["Completion page", "This page controls the post-submission experience.", '[data-tour="campaign-completion-shell"]'],
    ["Completion content", "Edit the thank-you message and final content supporters see after they complete the campaign.", '[data-tour="campaign-completion-content"]'],
    ["Final actions", "Save completion page changes here after editing.", '[data-tour="campaign-completion-actions"]'],
    ["Settings", "Next we will review campaign settings.", '[data-tour="campaign-builder-nav-settings"]']
  ],
  settings: [
    ["Settings", "These settings control campaign identity, discoverability, and operational behavior.", '[data-tour="campaign-settings-shell"]'],
    ["Basics", "Edit the title, SEO description, public owner name, and core campaign details here.", '[data-tour="campaign-settings-basics"]'],
    ["Public URL", "The slug controls the public campaign URL. Be careful changing it after links have been shared.", '[data-tour="campaign-settings-publishing"]'],
    ["Campaign type", "Campaign type changes which workflow and features this campaign uses.", '[data-tour="campaign-settings-sharing"]'],
    ["Questions", "Next we will look at the advanced question editor.", '[data-tour="campaign-builder-nav-questions"]']
  ],
  questions: [
    ["Questions", "Use this tab when you need detailed control over question setup, editing, and ordering.", '[data-tour="campaign-questions-shell"]'],
    ["Automation", "Next we will look at automations that run from campaign responses.", '[data-tour="campaign-builder-nav-automation"]']
  ],
  automation: [
    ["Automation", "Automations let the campaign send emails or create tasks from submitted responses.", '[data-tour="campaign-automation-shell"]'],
    ["Email automations", "Use email automations to contact supporters or alert your team.", '[data-tour="campaign-automation-email"]'],
    ["Task automations", "Use task automations to create follow-up work for admins.", '[data-tour="campaign-automation-tasks"]'],
    ["Analytics", "Next we will check reporting for this campaign.", '[data-tour="campaign-builder-nav-analytics"]']
  ],
  analytics: [
    ["Analytics", "This tab shows campaign performance and reporting for the selected campaign.", '[data-tour="campaign-analytics-shell"]'],
    ["Responses", "Finally, we will look at the response list.", '[data-tour="campaign-builder-nav-responses"]']
  ],
  responses: [
    ["Responses", "This is where submitted responses are reviewed, filtered, exported, and opened in detail.", '[data-tour="campaign-responses-shell"]']
  ]
}

function parseBool(value) {
  try {
    return JSON.parse(value)
  } catch (_e) {
    return value === true || value === 'true'
  }
}

function visible(selector) {
  const el = document.querySelector(selector)
  return el && !el.classList.contains('hidden')
}

function stepsFor(tab) {
  return (TAB_STEPS[tab] || []).map(normalizeStep).filter(step => visible(step.selector))
}

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.getAttribute('content')
}

function completeTour(root, pushEvent) {
  if (pushEvent) {
    pushEvent('complete-admin-campaign-builder-tour', {})
    return
  }

  const url = root?.dataset.completeUrl
  const token = csrfToken()
  if (!url || !token) return

  window.fetch(url, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      'x-csrf-token': token
    },
    body: '{}',
    credentials: 'same-origin'
  })
}

function normalizeStep(step) {
  if (Array.isArray(step)) {
    const [title, text, selector] = step
    return { title, text, selector }
  }

  return step
}

function makeStep(tour, stepConfig, isFinalStep, pushEvent) {
  const { title, text, selector } = stepConfig
  const step = stepConfig.interactive
    ? interactiveElStep(title, text, selector, isFinalStep, stepConfig)
    : highlightElStep(tour, title, text, selector, 'bottom', isFinalStep)

  if (isFinalStep) {
    step.buttons = [
      {
        action() {
          const root = document.querySelector(TOUR_SELECTOR)
          const complete = parseBool(root?.dataset.completeOnFinish)

          if (complete) {
            completeTour(root, pushEvent)
            this.complete()
          } else {
            const nextUrl = root?.dataset.nextUrl
            if (nextUrl) {
              window.location.assign(nextUrl)
            } else {
              this.complete()
            }
          }
        },
        text: finalButtonText()
      }
    ]
  }

  return step
}

function interactiveElStep(title, text, selector, isFinalStep, stepConfig) {
  return {
    title,
    text,
    attachTo: { element: selector, on: 'bottom' },
    modalOverlayOpeningPadding: 2,
    buttons: interactiveButtons(isFinalStep, stepConfig),
    beforeShowPromise: () => waitForInteractiveElement(selector)
  }
}

function interactiveButtons(isFinalStep, stepConfig) {
  const nextButton = {
    action() {
      if (!stepIsValid(stepConfig)) return

      if (isFinalStep) {
        const root = document.querySelector(TOUR_SELECTOR)
        const nextUrl = root?.dataset.nextUrl

        if (nextUrl) {
          window.location.assign(nextUrl)
        } else {
          this.complete()
        }
      } else {
        return this.next()
      }
    },
    text: isFinalStep ? finalButtonText() : 'Next'
  }

  if (isFinalStep) return [nextButton]

  return [
    {
      action() {
        return this.back()
      },
      classes: 'shepherd-button-secondary',
      text: 'Back'
    },
    nextButton
  ]
}

function stepIsValid(stepConfig) {
  const el = document.querySelector(stepConfig.validateSelector || stepConfig.selector)
  const value = (el?.value || '').trim()
  const valid = stepConfig.validate
    ? stepConfig.validate(el)
    : !stepConfig.required || value.length > 0

  const patternValid = !stepConfig.pattern || stepConfig.pattern.test(value)

  if (valid && patternValid) return true

  el?.focus?.()
  showStepValidation(stepConfig.invalidText || 'Complete this field before continuing.')
  return false
}

function showStepValidation(message) {
  const footer = document.querySelector('.shepherd-footer')
  if (!footer) return

  let error = footer.querySelector('[data-tour-validation]')
  if (!error) {
    error = document.createElement('div')
    error.dataset.tourValidation = 'true'
    error.style.color = '#b91c1c'
    error.style.fontSize = '0.875rem'
    error.style.marginRight = 'auto'
    footer.prepend(error)
  }

  error.textContent = message
}

function waitForInteractiveElement(selector) {
  return waitForVisibleElement(selector).then(() => {
    const el = document.querySelector(selector)
    window.setTimeout(() => el?.focus?.(), 100)
    return true
  })
}

function waitForVisibleElement(selector) {
  if (visible(selector)) return Promise.resolve(true)

  return new Promise(requestAnimationFrame).then(() => waitForVisibleElement(selector))
}

function finalButtonText() {
  const root = document.querySelector(TOUR_SELECTOR)

  if (parseBool(root?.dataset.completeOnFinish)) return 'Done'
  if (root?.dataset.nextUrl) return 'Next'

  return 'Got it'
}

export function startAdminCampaignBuilderTour(root, pushEvent = null) {
  if (!root || !parseBool(root.dataset.shouldRun)) return
  if (root.dataset.started === 'true') return
  root.dataset.started = 'true'

  const tab = root.dataset.currentTab || 'editor'
  const rawSteps = stepsFor(tab)
  if (rawSteps.length === 0) {
    root.dataset.started = 'false'
    return
  }

  const tour = makeModalTour()
  const steps = rawSteps.map((step, index) => makeStep(tour, step, index === rawSteps.length - 1, pushEvent))

  tour.addSteps(steps)
  tour.on('cancel', () => completeTour(root, pushEvent))
  tour.start()
}

export function setupAdminCampaignBuilderTour() {
  const root = document.querySelector(TOUR_SELECTOR)
  if (root?.getAttribute('phx-hook')) return
  startAdminCampaignBuilderTour(root)
}

export const AdminCampaignBuilderTourHook = {
  mounted() {
    startAdminCampaignBuilderTour(this.el, this.pushEvent.bind(this))
  },
  updated() {
    startAdminCampaignBuilderTour(this.el, this.pushEvent.bind(this))
  }
}
